import React, { useState, useEffect } from 'react';
import { glassSurface } from './config/glassTheme';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API from './api';
import ProductNav from './components/ProductNav';

// -------------------------------------------------------------------
// SLIDER DEFINITIONS — confirmed option values from live engine only
// -------------------------------------------------------------------

const STYLE_STATES = [
  { label: 'Painterly',  desc: 'Oil, watercolour, illustration',
    values: { renderStyle: 'Painterly Illustration' } },
  { label: 'Digital',    desc: 'Cinematic digital art',
    values: { renderStyle: 'Cinematic Digital Art' } },
  { label: 'Graphic',    desc: 'Graphic novel, poster',
    values: { renderStyle: 'Graphic Novel Style' } },
  { label: 'Technical',  desc: 'Isometric, blueprint, tactical',
    values: { renderStyle: 'Isometric Illustration' } },
  { label: '3D Render',  desc: 'Photoreal or stylised 3D',
    values: { renderStyle: '3D Photoreal Render' } },
  { label: 'Retro',      desc: 'Pixel art, vintage, card art',
    values: { renderStyle: 'Fantasy Card Art' } },
];

const DETAIL_STATES = [
  { label: 'Clean',     desc: 'Minimal, simplified output',
    values: { detailIntensity: 'Minimal Detail' } },
  { label: 'Balanced',  desc: 'Standard production detail',
    values: { detailIntensity: 'Balanced Detail' } },
  { label: 'Rich',      desc: 'High density, complex surfaces',
    values: { detailIntensity: 'Rich Detail' } },
  { label: 'Micro',     desc: 'Extreme surface-level detail',
    values: { detailIntensity: 'Micro Detail' } },
  { label: 'Surface',   desc: 'Texture and material emphasis',
    values: { detailIntensity: 'Texture Emphasis' } },
  { label: 'Showcase',  desc: 'Masterwork, architectural precision',
    values: { detailIntensity: 'Showcase Finish' } },
];

const CAMERA_STATES = [
  { label: 'Expansive', desc: 'Wide, elevated, vast',
    values: { camera: 'Elevated Forward View', framing: 'Vast Scene View', lensAndPerspective: 'Ultra Wide', depthOfField: 'Infinite Focus' } },
  { label: 'Natural',   desc: 'Eye-level, full body',
    values: { camera: 'Neutral Eye-Level', framing: 'Full Body', lensAndPerspective: 'Natural Lens', depthOfField: 'Natural Depth' } },
  { label: 'Grounded',  desc: 'Slight upward, three-quarter',
    values: { camera: 'Slight Upward Tilt', framing: 'Three-Quarter Body', lensAndPerspective: 'Standard Lens', depthOfField: 'Scene Clarity' } },
  { label: 'Cinematic', desc: 'Low angle, anamorphic',
    values: { camera: 'Low Angle Upward', framing: 'Medium Shot', lensAndPerspective: 'Anamorphic Lens', depthOfField: 'Cinematic Blur' } },
  { label: 'Portrait',  desc: 'Head and shoulders, shallow',
    values: { camera: 'Neutral Eye-Level', framing: 'Head and Shoulders', lensAndPerspective: 'Portrait Lens', depthOfField: 'Shallow Depth Of Field' } },
  { label: 'Extreme',   desc: 'Dutch angle, fisheye',
    values: { camera: 'Tilted Angle (Dutch)', framing: 'Extreme Close-Up', lensAndPerspective: 'Fisheye Lens', depthOfField: 'Heavy Bokeh' } },
];

const SCALE_STATES = [
  { label: 'Intimate',    desc: 'Single character or object',
    values: { scale: 'Single Character', framing: 'Full Body' } },
  { label: 'Street',      desc: 'Street section, plaza level',
    values: { scale: 'Street Section', framing: 'Subject in Environment' } },
  { label: 'Structural',  desc: 'Building, fortress, tower',
    values: { scale: 'Large Structure', framing: 'Wide Subject Frame' } },
  { label: 'Settlement',  desc: 'Town, city district overview',
    values: { scale: 'City District', framing: 'Vast Scene View' } },
  { label: 'Landscape',   desc: 'Valley, regional, world view',
    values: { scale: 'Landscape View', framing: 'Vast Scene View' } },
  { label: 'Epic',        desc: 'Battlefield, monument, world map',
    values: { scale: 'Battlefield Scale', framing: 'Vast Scene View' } },
];

const FOCUS_STATES = [
  { label: 'Subject',      desc: 'Primary subject dominant',
    values: { subjectEmphasis: 'Primary Subject Focus', depthOfField: 'Shallow Depth Of Field' } },
  { label: 'Balanced',     desc: 'Subject and environment shared',
    values: { subjectEmphasis: 'Subject-Environment Balance', depthOfField: 'Natural Depth' } },
  { label: 'Environment',  desc: 'Scene and setting dominant',
    values: { subjectEmphasis: 'Environment Priority', depthOfField: 'Deep Focus' } },
  { label: 'Multi',        desc: 'Multiple focus points',
    values: { subjectEmphasis: 'Multiple Focus Points', depthOfField: 'Balanced Focus' } },
  { label: 'Foreground',   desc: 'Near-field priority',
    values: { subjectEmphasis: 'Foreground Emphasis', depthOfField: 'Foreground Focus' } },
  { label: 'Background',   desc: 'Deep scene focus',
    values: { subjectEmphasis: 'Background Emphasis', depthOfField: 'Infinite Focus' } },
];

const LIGHTING_STATES = [
  { label: 'Neutral',      desc: 'Flat, even, unbiased',
    values: { lighting: 'Overcast Light', colourPalette: 'Natural Balanced Palette', mood: 'Neutral', atmosphereEffects: 'Clear Atmosphere' } },
  { label: 'Soft',         desc: 'Natural, diffused, gentle',
    values: { lighting: 'Soft Daylight', colourPalette: 'Earth Tone Palette', mood: 'Calm', atmosphereEffects: 'Light Mist' } },
  { label: 'Bright',       desc: 'High key, warm, open',
    values: { lighting: 'High Key Lighting', colourPalette: 'Warm Colour Palette', mood: 'Cheerful', atmosphereEffects: 'Crisp Air' } },
  { label: 'Dramatic',     desc: 'Spotlight, dark, intense',
    values: { lighting: 'Dramatic Spotlight', colourPalette: 'Dark Palette', mood: 'Intense', atmosphereEffects: 'Smoke Haze' } },
  { label: 'Atmospheric',  desc: 'Golden hour, mysterious',
    values: { lighting: 'Golden Hour', colourPalette: 'Ember Palette', mood: 'Mysterious', atmosphereEffects: 'Morning Mist' } },
  { label: 'Extreme',      desc: 'Low key, void, ominous',
    values: { lighting: 'Low Key Lighting', colourPalette: 'Void Palette', mood: 'Ominous', atmosphereEffects: 'Thick Fog' } },
];

const ATMOSPHERE_STATES = [
  { label: 'Clear',      desc: 'Clean, crisp, still air',
    values: { atmosphereEffects: 'Clear Atmosphere' } },
  { label: 'Mist',       desc: 'Morning mist, light fog',
    values: { atmosphereEffects: 'Light Mist' } },
  { label: 'Weather',    desc: 'Rain, snow, sleet',
    values: { atmosphereEffects: 'Rainfall' } },
  { label: 'Particles',  desc: 'Dust, ash, pollen drift',
    values: { atmosphereEffects: 'Dust Particles' } },
  { label: 'Smoke',      desc: 'Battlefield smoke, embers',
    values: { atmosphereEffects: 'Smoke Haze' } },
  { label: 'Magical',    desc: 'Arcane particles, ether mist',
    values: { atmosphereEffects: 'Arcane Particles' } },
];

const OUTPUT_OPTIONS    = [1, 2, 4];
const CREDIT_MULTIPLIER = 2.0;
const BASE_CREDIT_COST  = 1;

function calcCost(outputCount) {
  return Math.ceil(BASE_CREDIT_COST * CREDIT_MULTIPLIER * outputCount);
}

// -------------------------------------------------------------------
// Global interaction CSS — injected once
// -------------------------------------------------------------------

const INTERACTION_CSS = `
  .qe-tick-btn {
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease, transform 0.12s ease;
  }
  .qe-tick-btn:hover {
    background: rgba(139,92,246,0.18) !important;
    border-color: rgba(139,92,246,0.45) !important;
    color: rgba(255,255,255,0.85) !important;
    transform: translateY(-1px);
  }
  .qe-tick-btn:active {
    transform: scale(0.96) !important;
  }
  .qe-tick-btn.qe-tick-active {
    background: rgba(139,92,246,0.22) !important;
    box-shadow: 0 0 0 1px rgba(139,92,246,0.55), 0 2px 8px rgba(139,92,246,0.25) !important;
    transform: none !important;
  }
  .qe-tick-btn.qe-tick-active:hover {
    background: rgba(139,92,246,0.30) !important;
    transform: none !important;
  }
  .qe-out-btn {
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease, transform 0.12s ease;
  }
  .qe-out-btn:hover {
    border-color: rgba(139,92,246,0.45) !important;
    color: rgba(255,255,255,0.85) !important;
    background: rgba(139,92,246,0.15) !important;
    transform: translateY(-1px);
    box-shadow: 0 3px 10px rgba(0,0,0,0.25);
  }
  .qe-out-btn:active {
    transform: scale(0.96) !important;
  }
  .qe-gen-btn {
    transition: filter 0.15s ease, box-shadow 0.15s ease, transform 0.12s ease;
  }
  .qe-gen-btn:hover {
    filter: brightness(1.12);
    box-shadow: 0 6px 24px rgba(139,92,246,0.55) !important;
    transform: translateY(-1px);
  }
  .qe-gen-btn:active {
    filter: brightness(0.92) !important;
    transform: scale(0.98) !important;
    box-shadow: 0 2px 8px rgba(139,92,246,0.3) !important;
  }
  .qe-range-input {
    -webkit-appearance: none;
    appearance: none;
    height: 5px;
    border-radius: 3px;
    background: rgba(255,255,255,0.12);
    outline: none;
    cursor: pointer;
  }
  .qe-range-input::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: linear-gradient(135deg, #8b5cf6, #a855f7);
    cursor: pointer;
    box-shadow: 0 0 0 2px rgba(139,92,246,0.3), 0 2px 6px rgba(0,0,0,0.35);
    transition: box-shadow 0.15s ease, transform 0.12s ease;
  }
  .qe-range-input::-webkit-slider-thumb:hover {
    box-shadow: 0 0 0 3px rgba(139,92,246,0.5), 0 3px 10px rgba(139,92,246,0.4);
    transform: scale(1.15);
  }
  .qe-range-input::-webkit-slider-thumb:active {
    transform: scale(1.05);
    box-shadow: 0 0 0 4px rgba(139,92,246,0.4), 0 2px 8px rgba(139,92,246,0.5);
  }
  .qe-range-input::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: linear-gradient(135deg, #8b5cf6, #a855f7);
    cursor: pointer;
    border: none;
    box-shadow: 0 0 0 2px rgba(139,92,246,0.3), 0 2px 6px rgba(0,0,0,0.35);
  }
`;

// -------------------------------------------------------------------
// QuickSlider component
// -------------------------------------------------------------------

function QuickSlider({ label, states, value, onChange, accentColor }) {
  const state = states[value];
  return (
    <div style={sl.wrap}>
      <div style={sl.header}>
        <span style={sl.label}>{label}</span>
        <span style={{ ...sl.stateName, color: accentColor }}>{state.label}</span>
        <span style={sl.stateDesc}>{state.desc}</span>
      </div>
      <div style={sl.trackWrap}>
        <input
          type="range"
          min={0}
          max={states.length - 1}
          value={value}
          onChange={e => onChange(Number(e.target.value))}
          className="qe-range-input"
          style={sl.range}
        />
        <div style={sl.ticks}>
          {states.map((s, i) => (
            <button
              key={i}
              className={`qe-tick-btn${i === value ? ' qe-tick-active' : ''}`}
              style={{
                ...sl.tick,
                ...(i === value ? {
                  ...sl.tickActive,
                  borderColor: accentColor,
                  color: accentColor,
                } : {}),
              }}
              onClick={() => onChange(i)}
              title={s.label}
            >
              <span style={sl.tickLabel}>{s.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div style={sl.values}>
        {Object.entries(state.values).map(([k, v]) => (
          <div key={k} style={sl.valueRow}>
            <span style={sl.valueKey}>{k}</span>
            <span style={sl.valueVal}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const sl = {
  wrap:       { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 12, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 },
  header:     { display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' },
  label:      { fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.4)', minWidth: 72 },
  stateName:  { fontSize: 15, fontWeight: 800 },
  stateDesc:  { fontSize: 12, color: 'rgba(255,255,255,0.38)', fontStyle: 'italic' },
  trackWrap:  { display: 'flex', flexDirection: 'column', gap: 8 },
  range:      { width: '100%' },
  ticks:      { display: 'flex', justifyContent: 'space-between', gap: 4 },
  tick: {
    flex: 1,
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 5,
    padding: '5px 2px',
    cursor: 'pointer',
    fontSize: 10,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.45)',
    textAlign: 'center',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)',
  },
  tickActive: {
    background: 'rgba(139,92,246,0.18)',
    fontWeight: 800,
    boxShadow: '0 0 0 1px rgba(139,92,246,0.5), 0 2px 8px rgba(139,92,246,0.2)',
  },
  tickLabel:  { display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  values:     { display: 'flex', flexWrap: 'wrap', gap: '4px 12px' },
  valueRow:   { display: 'flex', gap: 5, alignItems: 'baseline' },
  valueKey:   { fontSize: 10, color: 'rgba(255,255,255,0.25)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' },
  valueVal:   { fontSize: 11, color: 'rgba(255,255,255,0.55)' },
};

// -------------------------------------------------------------------
// Section header
// -------------------------------------------------------------------

function SectionHeader({ label }) {
  return (
    <div style={sh.wrap}>
      <div style={sh.line} />
      <span style={sh.label}>{label}</span>
      <div style={sh.line} />
    </div>
  );
}

const sh = {
  wrap:  { display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0 2px' },
  line:  { flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' },
  label: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(255,255,255,0.28)', whiteSpace: 'nowrap' },
};

// -------------------------------------------------------------------
// Main QuickEditPage
// -------------------------------------------------------------------

export default function QuickEditPage() {
  const navigate      = useNavigate();
  const [searchParams] = useSearchParams();
  const buildId        = searchParams.get('build_id');

  const [build,         setBuild]         = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [imgLoaded,     setImgLoaded]     = useState(false);

  const [stylePos,      setStylePos]      = useState(1);
  const [detailPos,     setDetailPos]     = useState(1);
  const [cameraPos,     setCameraPos]     = useState(1);
  const [scalePos,      setScalePos]      = useState(0);
  const [focusPos,      setFocusPos]      = useState(1);
  const [lightingPos,   setLightingPos]   = useState(0);
  const [atmospherePos, setAtmospherePos] = useState(0);
  const [outputCount,   setOutputCount]   = useState(1);

  const cost = calcCost(outputCount);

  // Inject interaction CSS once
  useEffect(() => {
    if (document.getElementById('qe-interaction-css')) return;
    const el = document.createElement('style');
    el.id          = 'qe-interaction-css';
    el.textContent = INTERACTION_CSS;
    document.head.appendChild(el);
  }, []);

  useEffect(() => {
    if (!buildId) { setError('No build selected.'); setLoading(false); return; }
    API.get(`/v1/images/jobs/${buildId}`)
      .then(res => {
        const job = res.data;
        if (!job) { setError('Build not found.'); return; }
        if (job.status !== 'completed' || !job.result?.url) {
          setError('This build does not have a completed image.');
          return;
        }
        setBuild({
          build_id:  job.job_id,
          slug:      job.generator_slug || '',
          image_url: job.result.url,
          build_name: job.generator_slug
            ? job.generator_slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
            : 'Build',
        });
      })
      .catch(() => setError('Failed to load build.'))
      .finally(() => setLoading(false));
  }, [buildId]);

  const sliderPositions = {
    style:      stylePos,
    detail:     detailPos,
    camera:     cameraPos,
    scale:      scalePos,
    focus:      focusPos,
    lighting:   lightingPos,
    atmosphere: atmospherePos,
  };

  if (loading) return (
    <div style={qe.page}>
      <ProductNav />
      <div style={qe.center}><div style={qe.spinner} /></div>
    </div>
  );

  if (error) return (
    <div style={qe.page}>
      <ProductNav />
      <div style={qe.center}>
        <div style={qe.errorBox}>
          <div style={qe.errorTitle}>Cannot open Quick Edit</div>
          <div style={qe.errorMsg}>{error}</div>
          <button style={qe.backBtn} onClick={() => navigate('/workspace')}>&larr; Back to Workspace</button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={qe.page}>
      <ProductNav />
      <div style={qe.container}>

        <div style={qe.header}>
          <div>
            <h1 style={qe.title}>Quick Edit</h1>
            <p style={qe.subtitle}>{build.build_name} &mdash; adjust sliders, then generate variations.</p>
          </div>
          <button style={qe.backBtn} onClick={() => navigate('/workspace')}>&larr; Workspace</button>
        </div>

        <div style={qe.layout}>

          {/* LEFT — image preview + notice */}
          <div style={qe.previewCol}>
            <div style={qe.previewCard}>
              <div style={qe.previewLabel}>Source Image</div>
              <div style={qe.imgWrap}>
                {!imgLoaded && <div style={qe.imgSkeleton} />}
                <img
                  src={build.image_url}
                  alt={build.build_name}
                  style={{ ...qe.img, ...(imgLoaded ? {} : { opacity: 0, position: 'absolute' }) }}
                  onLoad={() => setImgLoaded(true)}
                />
              </div>
              <div style={qe.imgMeta}>
                <span style={qe.imgMetaLabel}>Build ID</span>
                <span style={qe.imgMetaVal}>{build.build_id.slice(0, 12)}...</span>
              </div>
              <div style={qe.imgMeta}>
                <span style={qe.imgMetaLabel}>Generator</span>
                <span style={qe.imgMetaVal}>{build.slug || 'Unknown'}</span>
              </div>
            </div>

            <div style={qe.noticeCard}>
              <div style={qe.noticeTitle}>&#9888; Premium Feature</div>
              <div style={qe.noticeBody}>
                Quick Edit uses a <strong style={{ color: '#fcd34d' }}>2x credit multiplier</strong> per output.
                Slider values are applied server-side to your original build configuration.
              </div>
              <div style={qe.noticeDivider} />
              <div style={qe.noticeBody}>
                <strong style={{ color: '#fff' }}>Refunds:</strong> granted only if generation fails after 2 attempts or outputs are missing. No subjective refunds.
              </div>
              <div style={qe.noticeDivider} />
              <div style={qe.noticeBody}>
                <strong style={{ color: '#fff' }}>Retries:</strong> up to 2 automatic retries on failure.
              </div>
            </div>
          </div>

          {/* RIGHT — sliders in 3 sections */}
          <div style={qe.controlsCol}>

            <SectionHeader label="Composition" />
            <QuickSlider label="Camera"     states={CAMERA_STATES}     value={cameraPos}     onChange={setCameraPos}     accentColor="#a78bfa" />
            <QuickSlider label="Scale"      states={SCALE_STATES}      value={scalePos}      onChange={setScalePos}      accentColor="#a78bfa" />
            <QuickSlider label="Focus"      states={FOCUS_STATES}      value={focusPos}      onChange={setFocusPos}      accentColor="#a78bfa" />

            <SectionHeader label="Visual Fidelity" />
            <QuickSlider label="Style"      states={STYLE_STATES}      value={stylePos}      onChange={setStylePos}      accentColor="#34d399" />
            <QuickSlider label="Detail"     states={DETAIL_STATES}     value={detailPos}     onChange={setDetailPos}     accentColor="#34d399" />

            <SectionHeader label="Atmosphere & Tone" />
            <QuickSlider label="Lighting"   states={LIGHTING_STATES}   value={lightingPos}   onChange={setLightingPos}   accentColor="#fcd34d" />
            <QuickSlider label="Atmosphere" states={ATMOSPHERE_STATES} value={atmospherePos} onChange={setAtmospherePos} accentColor="#fcd34d" />

            {/* Output count */}
            <div style={qe.outputSection}>
              <div style={qe.outputLabel}>Outputs</div>
              <div style={qe.outputBtns}>
                {OUTPUT_OPTIONS.map(n => (
                  <button
                    key={n}
                    className="qe-out-btn"
                    style={{
                      ...qe.outputBtn,
                      ...(outputCount === n ? qe.outputBtnActive : {}),
                    }}
                    onClick={() => setOutputCount(n)}
                  >
                    {n} image{n !== 1 ? 's' : ''}
                  </button>
                ))}
              </div>
            </div>

            {/* Cost display */}
            <div style={qe.costRow}>
              <div style={qe.costLeft}>
                <span style={qe.costLabel}>Credit cost</span>
                <span style={qe.costSub}>{outputCount} output{outputCount !== 1 ? 's' : ''} &times; {CREDIT_MULTIPLIER}x multiplier</span>
              </div>
              <div style={qe.costAmount}>{cost} <span style={qe.costUnit}>credits</span></div>
            </div>

            {/* Generate button */}
            <button
              className="qe-gen-btn"
              style={qe.generateBtn}
              onClick={() => {
                navigate('/generating', {
                  state: {
                    mode:             'quick_edit',
                    build_id:         build.build_id,
                    slug:             build.slug,
                    source_image_id:  build.build_id,
                    slider_positions: sliderPositions,
                    output_count:     outputCount,
                  },
                });
              }}
            >
              Generate {outputCount} Variation{outputCount !== 1 ? 's' : ''} &mdash; {cost} Credits
            </button>

            <div style={qe.footNote}>
              Slider values are merged server-side with your original configuration.
              Your source image is not modified.
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Styles
// -------------------------------------------------------------------

const qe = {
  page:        { minHeight: '100vh', background: 'linear-gradient(135deg, #1e1b4b 0%, #334155 50%, #881337 100%)', fontFamily: "'Segoe UI', sans-serif" },
  center:      { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' },
  spinner:     { width: 36, height: 36, border: '3px solid rgba(139,92,246,0.2)', borderTop: '3px solid #8b5cf6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  errorBox:    { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14, padding: 32, maxWidth: 400, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 12 },
  errorTitle:  { fontWeight: 800, fontSize: 18, color: '#fff' },
  errorMsg:    { fontSize: 14, color: 'rgba(255,255,255,0.55)' },
  container:   { maxWidth: 1200, margin: '0 auto', padding: '28px 32px' },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, flexWrap: 'wrap', gap: 16 },
  title:       { color: '#fff', fontSize: 26, fontWeight: 800, margin: '0 0 4px' },
  subtitle:    { color: 'rgba(255,255,255,0.5)', fontSize: 14, margin: 0 },
  backBtn:     { ...glassSurface('#334155', '#64748B', '#94A3B8'), borderRadius: 8, padding: '9px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' },
  layout:      { display: 'grid', gridTemplateColumns: '320px 1fr', gap: 24, alignItems: 'start' },
  previewCol:  { display: 'flex', flexDirection: 'column', gap: 16, position: 'sticky', top: 20 },
  previewCard: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  previewLabel:{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.35)', padding: '10px 14px 6px' },
  imgWrap:     { position: 'relative', width: '100%', paddingBottom: '100%', background: 'rgba(0,0,0,0.3)' },
  imgSkeleton: { position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.07) 50%, rgba(255,255,255,0.03) 100%)', backgroundSize: '200% 100%' },
  img:         { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transition: 'opacity 0.3s' },
  imgMeta:     { display: 'flex', gap: 8, padding: '5px 14px', alignItems: 'baseline', borderTop: '1px solid rgba(255,255,255,0.05)' },
  imgMetaLabel:{ fontSize: 10, color: 'rgba(255,255,255,0.28)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', minWidth: 68 },
  imgMetaVal:  { fontSize: 11, color: 'rgba(255,255,255,0.5)', fontFamily: 'monospace' },
  noticeCard:  { background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.18)', borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 },
  noticeTitle: { fontSize: 12, fontWeight: 800, color: '#fcd34d', textTransform: 'uppercase', letterSpacing: '0.06em' },
  noticeBody:  { fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 },
  noticeDivider: { height: 1, background: 'rgba(245,158,11,0.1)' },
  controlsCol: { display: 'flex', flexDirection: 'column', gap: 10 },
  outputSection: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 16, marginTop: 6 },
  outputLabel: { fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.4)', minWidth: 56 },
  outputBtns:  { display: 'flex', gap: 8 },
  outputBtn:   {
    background: 'rgba(255,255,255,0.06)',
    color: 'rgba(255,255,255,0.55)',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 8,
    padding: '8px 18px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.15)',
  },
  outputBtnActive: {
    background: 'linear-gradient(135deg, #7c3aed, #8b5cf6)',
    color: '#fff',
    border: '1px solid rgba(139,92,246,0.6)',
    boxShadow: '0 2px 10px rgba(139,92,246,0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
    fontWeight: 700,
  },
  costRow:     { background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 12, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  costLeft:    { display: 'flex', flexDirection: 'column', gap: 2 },
  costLabel:   { fontSize: 13, fontWeight: 700, color: '#fff' },
  costSub:     { fontSize: 11, color: 'rgba(255,255,255,0.4)' },
  costAmount:  { fontSize: 26, fontWeight: 900, color: '#a78bfa' },
  costUnit:    { fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.45)' },
  generateBtn: {
    background: 'linear-gradient(135deg, #6d28d9, #7c3aed, #8b5cf6)',
    color: '#fff',
    border: '1px solid rgba(139,92,246,0.5)',
    borderRadius: 10,
    padding: '16px 24px',
    fontSize: 15,
    fontWeight: 800,
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(139,92,246,0.4), inset 0 1px 0 rgba(255,255,255,0.08)',
    letterSpacing: '0.01em',
    width: '100%',
  },
  footNote:    { fontSize: 11, color: 'rgba(255,255,255,0.28)', textAlign: 'center', lineHeight: 1.6 },
};
